import type { ScheduledMeeting } from '../../../contexts';
import {
  filterPersonalMeetingsFromModal,
  filterRestPersonalMeetings,
  getMeetingDisplayTitle,
} from '../../PersonalMeetingsModal/helpers/common';
import { sortPersonalMeetings } from '../../PersonalMeetingsModal/helpers/sorters';
import type { MeetingIdOption } from '../types';
import {
  DEFAULT_MEETING_OPTION_LABEL,
  GENERATE_UNIQUE_MEETING_ID,
  GENERATE_UNIQUE_MEETING_ID_LABEL,
} from './constants';

/**
 * Personal meetings in the same order the Personal Meetings sidebar lists them:
 * meetings created from that modal first, then the rest, each sorted by title.
 * Draft rooms are excluded — they have no title to show in the dropdown.
 *
 * The default meeting is kept whether or not it still has a title: its option is
 * labelled `DEFAULT_MEETING_OPTION_LABEL`, so it needs none. Dropping it would
 * desync the dropdown from the form, which is scheduling that very room.
 */
export const getPersonalMeetings = (
  meetings: ScheduledMeeting[],
  defaultMeetingId?: string,
): ScheduledMeeting[] => {
  const defaultMeeting = defaultMeetingId
    ? meetings.find((meeting) => meeting.room === defaultMeetingId)
    : undefined;
  const rest = meetings.filter((meeting) => meeting !== defaultMeeting);

  return [
    ...(defaultMeeting ? [defaultMeeting] : []),
    ...sortPersonalMeetings(rest.filter(filterPersonalMeetingsFromModal)),
    ...sortPersonalMeetings(rest.filter(filterRestPersonalMeetings)),
  ];
};

/**
 * Personal rooms first (default, then the rest), Generate last. The closed
 * control selects the default personal meeting when one exists, otherwise Generate.
 */
export const buildMeetingIdOptions = (
  personalMeetings: ScheduledMeeting[],
  defaultMeetingId?: string,
): MeetingIdOption[] => {
  const defaultMeeting = defaultMeetingId
    ? personalMeetings.find((meeting) => meeting.room === defaultMeetingId)
    : undefined;

  const toOption = (meeting: ScheduledMeeting, isDefault: boolean): MeetingIdOption => ({
    value: meeting.room,
    label: isDefault ? DEFAULT_MEETING_OPTION_LABEL : getMeetingDisplayTitle(meeting),
    meetingId: meeting.room,
  });

  return [
    ...(defaultMeeting ? [toOption(defaultMeeting, true)] : []),
    ...personalMeetings
      .filter((meeting) => meeting.room !== defaultMeeting?.room)
      .map((meeting) => toOption(meeting, false)),
    {
      value: GENERATE_UNIQUE_MEETING_ID,
      label: GENERATE_UNIQUE_MEETING_ID_LABEL,
      isGenerate: true,
    },
  ];
};

/**
 * The dropdown selection is derived rather than stored: a room that belongs to a
 * personal meeting selects that option, anything else is an allocated unique room.
 */
export const getSelectedMeetingIdValue = (
  room: string,
  personalMeetings: ScheduledMeeting[],
): string =>
  room && personalMeetings.some((meeting) => meeting.room === room)
    ? room
    : GENERATE_UNIQUE_MEETING_ID;
